import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { LoadingBlock } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus, Save } from "lucide-react";
import { toast } from "sonner";

export default function Family() {
    const { user, setUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/profile").then(({ data }) => setProfile(data));
    }, []);

    if (!profile) return <LoadingBlock label="Loading profile" />;

    const update = (patch) => setProfile({ ...profile, ...patch });
    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                name: profile.name, phone: profile.phone, dob: profile.dob,
                pan_masked: profile.pan_masked, aadhaar_masked: profile.aadhaar_masked,
                address: profile.address, blood_group: profile.blood_group,
                family_members: profile.family_members || [],
                emergency_contacts: profile.emergency_contacts || [],
                professionals: profile.professionals || {},
            };
            const { data } = await api.put("/profile", payload);
            setProfile(data);
            setUser(data);
            toast.success("Profile saved");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const addFamily = () =>
        update({ family_members: [...(profile.family_members || []), { name: "", relation: "", phone: "" }] });
    const updFamily = (i, patch) => {
        const arr = [...(profile.family_members || [])];
        arr[i] = { ...arr[i], ...patch };
        update({ family_members: arr });
    };
    const delFamily = (i) => {
        const arr = [...(profile.family_members || [])];
        arr.splice(i, 1);
        update({ family_members: arr });
    };

    const addContact = () =>
        update({ emergency_contacts: [...(profile.emergency_contacts || []), { name: "", relation: "", phone: "" }] });
    const updContact = (i, patch) => {
        const arr = [...(profile.emergency_contacts || [])];
        arr[i] = { ...arr[i], ...patch };
        update({ emergency_contacts: arr });
    };
    const delContact = (i) => {
        const arr = [...(profile.emergency_contacts || [])];
        arr.splice(i, 1);
        update({ emergency_contacts: arr });
    };

    const pros = profile.professionals || {};
    const updPros = (patch) => update({ professionals: { ...pros, ...patch } });

    return (
        <div data-testid="family-page">
            <PageHeader
                eyebrow="Family & Professionals"
                title="The people behind your vault."
                subtitle="Your details, family, emergency contacts, and the trusted professionals around you."
                testid="family-header"
                actions={
                    <Button onClick={save} disabled={busy} data-testid="profile-save" className="rounded-full">
                        <Save className="mr-2 h-4 w-4" /> {busy ? "Saving…" : "Save changes"}
                    </Button>
                }
            />
            <div className="px-8 py-8 space-y-10 max-w-5xl">
                {/* Personal */}
                <section>
                    <h2 className="font-display text-xl font-semibold mb-4">Personal</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Full name" value={profile.name} onChange={(v) => update({ name: v })} testid="p-name" />
                        <Field label="Phone" value={profile.phone} onChange={(v) => update({ phone: v })} testid="p-phone" />
                        <Field label="Date of birth" type="date" value={profile.dob} onChange={(v) => update({ dob: v })} testid="p-dob" />
                        <Field label="Blood group" value={profile.blood_group} onChange={(v) => update({ blood_group: v })} testid="p-blood" />
                        <Field label="PAN (masked)" value={profile.pan_masked} onChange={(v) => update({ pan_masked: v })} testid="p-pan" placeholder="ABCDE****F" />
                        <Field label="Aadhaar (last 4)" value={profile.aadhaar_masked} onChange={(v) => update({ aadhaar_masked: v })} testid="p-aadhaar" placeholder="XXXX XXXX 1234" />
                        <div className="sm:col-span-2">
                            <Label>Address</Label>
                            <Textarea
                                data-testid="p-address"
                                value={profile.address || ""}
                                onChange={(e) => update({ address: e.target.value })}
                            />
                        </div>
                    </div>
                </section>

                {/* Family */}
                <section>
                    <div className="flex items-end justify-between mb-4">
                        <h2 className="font-display text-xl font-semibold">Family members</h2>
                        <Button variant="outline" size="sm" onClick={addFamily} data-testid="add-family"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                    </div>
                    <div className="space-y-3">
                        {(profile.family_members || []).length === 0 ? (
                            <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
                                Add spouse, children, parents — anyone the vault should know about.
                            </div>
                        ) : (profile.family_members || []).map((m, i) => (
                            <Row key={i} testid={`family-${i}`} onDel={() => delFamily(i)}>
                                <Field label="Name" value={m.name} onChange={(v) => updFamily(i, { name: v })} testid={`fm-${i}-name`} />
                                <Field label="Relation" value={m.relation} onChange={(v) => updFamily(i, { relation: v })} testid={`fm-${i}-rel`} />
                                <Field label="Phone" value={m.phone} onChange={(v) => updFamily(i, { phone: v })} testid={`fm-${i}-phone`} />
                                <Field label="DOB" type="date" value={m.dob} onChange={(v) => updFamily(i, { dob: v })} testid={`fm-${i}-dob`} />
                            </Row>
                        ))}
                    </div>
                </section>

                {/* Emergency contacts */}
                <section>
                    <div className="flex items-end justify-between mb-4">
                        <h2 className="font-display text-xl font-semibold">Emergency contacts</h2>
                        <Button variant="outline" size="sm" onClick={addContact} data-testid="add-contact"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                    </div>
                    <div className="space-y-3">
                        {(profile.emergency_contacts || []).length === 0 ? (
                            <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
                                Add at least 2 contacts your family can call in a crisis.
                            </div>
                        ) : (profile.emergency_contacts || []).map((c, i) => (
                            <Row key={i} testid={`contact-${i}`} onDel={() => delContact(i)}>
                                <Field label="Name" value={c.name} onChange={(v) => updContact(i, { name: v })} testid={`ec-${i}-name`} />
                                <Field label="Relation" value={c.relation} onChange={(v) => updContact(i, { relation: v })} testid={`ec-${i}-rel`} />
                                <Field label="Phone" value={c.phone} onChange={(v) => updContact(i, { phone: v })} testid={`ec-${i}-phone`} />
                            </Row>
                        ))}
                    </div>
                </section>

                {/* Professionals */}
                <section>
                    <h2 className="font-display text-xl font-semibold mb-4">Trusted professionals</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Financial advisor — name" value={pros.advisor_name} onChange={(v) => updPros({ advisor_name: v })} testid="pr-adv-n" />
                        <Field label="Financial advisor — phone" value={pros.advisor_phone} onChange={(v) => updPros({ advisor_phone: v })} testid="pr-adv-p" />
                        <Field label="CA — name" value={pros.ca_name} onChange={(v) => updPros({ ca_name: v })} testid="pr-ca-n" />
                        <Field label="CA — phone" value={pros.ca_phone} onChange={(v) => updPros({ ca_phone: v })} testid="pr-ca-p" />
                        <Field label="Doctor — name" value={pros.doctor_name} onChange={(v) => updPros({ doctor_name: v })} testid="pr-doc-n" />
                        <Field label="Doctor — phone" value={pros.doctor_phone} onChange={(v) => updPros({ doctor_phone: v })} testid="pr-doc-p" />
                        <Field label="Broker / Lawyer — name" value={pros.broker_name} onChange={(v) => updPros({ broker_name: v })} testid="pr-br-n" />
                        <Field label="Broker / Lawyer — phone" value={pros.broker_phone} onChange={(v) => updPros({ broker_phone: v })} testid="pr-br-p" />
                    </div>
                </section>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
    return (
        <div>
            <Label>{label}</Label>
            <Input
                type={type}
                value={value || ""}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
            />
        </div>
    );
}

function Row({ children, onDel, testid }) {
    return (
        <div data-testid={testid} className="rounded-lg border border-border bg-card p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 relative">
            {children}
            <button
                type="button"
                onClick={onDel}
                data-testid={`${testid}-del`}
                className="absolute top-3 right-3 text-destructive/80 hover:text-destructive"
            >
                <Trash2 className="h-4 w-4" />
            </button>
        </div>
    );
}
